import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setChats, setUnreadCount } from '../Redux/store';
import { incrementOperation, decrementOperation } from '../Redux/loadingSlice';
import { supabase } from '../supabase/client';
import { pullFromCache, saveToCache } from './chatsCache';

/**
 * ChatsLoader - Loads chats once and listens for real-time updates
 * 
 * Architecture:
 * 1. Load chats: Supabase → Redux (one-time)
 * 2. Realtime sub: listen for INSERT/UPDATE, update Redux by finding and replacing changed chat
 * 
 * Redux is source of truth for UI
 */
export default function ChatsLoader() {
  const dispatch = useDispatch();
  const authUserId = useSelector((s) => s.user.authUserId);
  const chats = useSelector((s) => s.chats.chats);
  const chatsRef = useRef(chats);

  // Sync chats to ref on every render to avoid closure trap
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Count chats where user hasn't read yet (new pattern: empty = unread, has userId = read)
  const calculateUnreadCount = (chatsObj) => {
    if (!authUserId) return 0;
    
    const unreadChats = Object.values(chatsObj).filter(chat => {
      const readBy = chat.read_by || {};
      const isUnread = !(authUserId in readBy);
      return isUnread;
    });
    
    return unreadChats.length;
  };

  // Load chats once: Cache → Redux (instant), then Supabase → Redux (background)
  useEffect(() => {
    if (!authUserId) return;

    const loadChats = async () => {
      // Pull from cache first (instant, no loading indicator)
      const cachedChats = pullFromCache();
      if (cachedChats) {
        dispatch(setChats(cachedChats));
        dispatch(setUnreadCount(calculateUnreadCount(cachedChats)));
      }
      
      // Load from Supabase with global loading indicator
      dispatch(incrementOperation('chats'));

      // Supabase: fresh data, sorted by most recent message
      const { data, error } = await supabase
        .from('chats')
        .select('*')
        .contains('user_ids', [authUserId])
        .order('last_message_time', { ascending: false, nullsFirst: false });

      if (error) {
        dispatch(decrementOperation('chats'));
        return;
      }

      // Transform array to object: { chatId: chat }
      if (data) {
        const chatsObj = {};
        data.forEach(chat => {
          chatsObj[chat.id] = chat;
        });
        dispatch(setChats(chatsObj));
        dispatch(setUnreadCount(calculateUnreadCount(chatsObj)));
        saveToCache(chatsObj);
      }

      dispatch(decrementOperation('chats'));
    };

    loadChats();
  }, [authUserId]); // Only run when authUserId changes

  // Realtime: listen for INSERT/UPDATE/DELETE changes to the user's chats (secured by RLS)
  // Note: No client-side filter used - RLS policies automatically filter events to only send
  // data the user is allowed to see. See docs/chats-realtime-rls.md for pattern details.
  useEffect(() => {
    if (!authUserId) return;

    // Dynamic channel name using the authUserId ensures a clean, isolated connection stream
    const channel = supabase
      .channel(`chats_changes:${authUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listens to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'chats',
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const currentChats = chatsRef.current;
            const updatedChats = { ...currentChats, [payload.new.id]: payload.new };
            
            dispatch(setChats(updatedChats));
            saveToCache(updatedChats);
            
            const newUnreadCount = calculateUnreadCount(updatedChats);
            dispatch(setUnreadCount(newUnreadCount));
          } else if (payload.eventType === 'DELETE') {
            // Handle deletes so removed chats disappear in realtime
            const currentChats = { ...chatsRef.current };
            delete currentChats[payload.old.id];
            
            dispatch(setChats(currentChats));
            saveToCache(currentChats);
            dispatch(setUnreadCount(calculateUnreadCount(currentChats)));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [authUserId]); // Restarts safely if authUserId changes

  return null;
}

// Manual reload: fetch from Supabase, update Redux + cache
export function reloadChats(dispatch, authUserId) {
  return async () => {
    if (!authUserId) {
      return;
    }

    dispatch(incrementOperation('chats'));

    const { data, error } = await supabase
      .from('chats')
      .select('*')
      .contains('user_ids', [authUserId])
      .order('last_message_time', { ascending: false, nullsFirst: false });

    if (error) {
      dispatch(decrementOperation('chats'));
      return;
    }

    // Transform and update
    if (data) {
      const chatsObj = {};
      data.forEach(chat => {
        chatsObj[chat.id] = chat;
      });
      dispatch(setChats(chatsObj));
      saveToCache(chatsObj);
    }

    dispatch(decrementOperation('chats'));
  };
}
